"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * SpotlightCard — surface wrapper with a cursor-following radial-glow overlay.
 *
 * Adapted from the React Bits Pro `@reactbits-starter/spotlight-card-tw`
 * pattern, re-implemented against pretty's anatomy:
 * - It wears the Onyx hex-notch silhouette (`nx-s`, the card scale). The two
 *   details a `clip-path` would break in the v1 way are handled per the surface
 *   recipe (issue #158): ELEVATION is a clip-safe `.elev-card` `drop-shadow`,
 *   NOT an outer `box-shadow` (which the notch clip-path slices away); and the
 *   OUTLINE is a continuous `nx-rim` RING overlay that traces the full notched
 *   silhouette including the chamfer, NOT a real CSS `border` (sliced at the
 *   notch diagonal). `overflow-hidden` keeps the spotlight gradient clipped to
 *   the notch — the glow can never bleed past the chamfer.
 * - Glow color drives from theme tokens (`--primary` / `--accent` / `--secondary`),
 *   never a hardcoded hex. Per-studio brand retinting via `<StudioBrand>` flows
 *   through automatically.
 * - `prefers-reduced-motion` honored: the cursor-tracked spotlight collapses
 *   to a centered static glow on hover, so the affordance stays visible
 *   without the live-tracking motion.
 * - SSR-safe — no `window` / `document` at module scope; cursor coords
 *   resolve inside `onMouseMove` only after first interaction.
 * - `forwardRef<HTMLDivElement>` for layout measurement.
 *
 * Use cases inside the AISquare platform:
 * - Quest cards, studio tile grids, featured-experience cards (replaces
 *   ad-hoc `hover:shadow-lg` + `hover:-translate-y` hover affordances).
 * - Aiko nudge surfaces — the spotlight reads as "the assistant is paying
 *   attention to where you are" without needing extra copy.
 *
 *     <SpotlightCard intensity="default" color="primary">
 *       <h3>Aiko's Studio</h3>
 *       <p>Live now</p>
 *     </SpotlightCard>
 *
 * Composition: wrap interactive elements (Link, button) inside, not around.
 * The outer card owns the spotlight + hover state; nested links/buttons keep
 * their own focus rings and click handling. `asChild` is intentionally not
 * supported in v1 — the two-layer overlay structure (glow + content) doesn't
 * collapse cleanly into a single Slot child. Track follow-up if a consumer
 * needs the outer element to be a Link.
 */

/**
 * Per-intensity opacity applied to the spotlight gradient. Picked so each
 * step is visible against `bg-card` without overwhelming the content layer.
 */
const INTENSITY_OPACITY: Record<NonNullable<SpotlightCardProps["intensity"]>, number> = {
  subtle: 0.08,
  default: 0.15,
  strong: 0.25,
};

/**
 * Per-color CSS-var token that drives the spotlight's hue. Reading
 * `hsl(var(--primary) / X)` lets per-studio retinting cascade in.
 */
const COLOR_TOKEN: Record<NonNullable<SpotlightCardProps["color"]>, string> = {
  primary: "--primary",
  accent: "--accent",
  secondary: "--secondary",
};

const spotlightCardVariants = cva(
  // Notched card surface: `nx-s` clips the hex-notch silhouette, `.elev-card` is
  // the clip-safe drop-shadow (a box-shadow would be sliced away by the notch),
  // and the outline is the `nx-rim` keyline ring below — never a real border.
  // `overflow-hidden` keeps the cursor-tracked spotlight gradient inside the
  // notch (it cannot bleed past the chamfer).
  [nx.s, "group/spotlight relative overflow-hidden bg-card text-card-foreground elev-card"].join(
    " ",
  ),
  {
    variants: {
      // `radius` is kept for API compatibility. The `nx-s` clip-path governs the
      // outer silhouette; this maps to the `@supports`-fallback corner radius
      // where `shape()` is unsupported.
      radius: {
        none: "rounded-none",
        sm: "rounded-md",
        default: "rounded-lg",
        lg: "rounded-xl",
        xl: "rounded-2xl",
      },
      padding: {
        none: "",
        sm: "p-3",
        default: "p-5",
        lg: "p-7",
      },
    },
    defaultVariants: {
      radius: "default",
      padding: "default",
    },
  },
);

export interface SpotlightCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spotlightCardVariants> {
  /** Spotlight glow strength. `subtle` for dense grids, `strong` for hero cards. */
  intensity?: "subtle" | "default" | "strong";
  /** Theme token that drives the glow color. Defaults to `primary`. */
  color?: "primary" | "accent" | "secondary";
  /**
   * Glow spread in pixels (the radius of the radial gradient). Larger values
   * make the spotlight feel softer and farther-reaching. Default 400px.
   */
  spread?: number;
}

export const SpotlightCard = React.forwardRef<HTMLDivElement, SpotlightCardProps>(
  (
    {
      className,
      radius,
      padding,
      intensity = "default",
      color = "primary",
      spread = 400,
      onMouseMove,
      onMouseEnter,
      onMouseLeave,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const [hovered, setHovered] = React.useState(false);
    // Default position keeps the static glow centered for reduced-motion users
    // and avoids a top-left flash before the first mousemove fires.
    const [pos, setPos] = React.useState<{ x: string; y: string }>({
      x: "50%",
      y: "50%",
    });

    const handleMouseMove = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (reduced !== true) {
          const rect = event.currentTarget.getBoundingClientRect();
          setPos({
            x: `${event.clientX - rect.left}px`,
            y: `${event.clientY - rect.top}px`,
          });
        }
        onMouseMove?.(event);
      },
      [reduced, onMouseMove],
    );

    const handleMouseEnter = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        setHovered(true);
        onMouseEnter?.(event);
      },
      [onMouseEnter],
    );

    const handleMouseLeave = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        setHovered(false);
        onMouseLeave?.(event);
      },
      [onMouseLeave],
    );

    const overlayBackground = `radial-gradient(circle ${spread}px at ${pos.x} ${pos.y}, hsl(var(${COLOR_TOKEN[color]}) / ${INTENSITY_OPACITY[intensity]}), transparent 60%)`;

    return (
      <div
        ref={ref}
        className={cn(spotlightCardVariants({ radius, padding }), className)}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={style}
        {...props}
      >
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 transition-opacity duration-300",
            hovered ? "opacity-100" : "opacity-0",
          )}
          style={{ background: overlayBackground }}
          data-spotlight-overlay
        />
        {/* Continuous keyline — a 1px `nx-rim` ring clipped to the notched
            silhouette, NOT a CSS border (which the notch clip-path would slice
            at the chamfer). Sits ABOVE the spotlight glow so the edge stays
            crisp as the gradient sweeps under it. */}
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0 z-[1] [--nx-rim:hsl(var(--border))]"
          style={{ clipPath: NX_RING_CLIP.s }}
        />
        <div className="relative">{children}</div>
      </div>
    );
  },
);
SpotlightCard.displayName = "SpotlightCard";

export { spotlightCardVariants };
